import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUrl, mockAuthHeaders } from '../lib/api';

interface AdminSettings {
  lunchStartTime: string;
  lunchDurationMinutes: number;
}

export function useAdminSettings() {
  return useQuery<AdminSettings>({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const response = await fetch(apiUrl('/api/v1/admin/settings'), {
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders(),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const detail = (errorBody as Record<string, Record<string, string>>)?.error?.message || response.statusText;
        throw new Error(detail);
      }

      const json = await response.json();
      return (json as Record<string, unknown>).data as AdminSettings;
    },
  });
}

export function useUpdateAdminSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Partial<AdminSettings>) => {
      const response = await fetch(apiUrl('/api/v1/admin/settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const detail = (errorBody as Record<string, Record<string, string>>)?.error?.message || response.statusText;
        throw new Error(detail);
      }

      const json = await response.json();
      return (json as Record<string, unknown>).data as AdminSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
    },
  });
}
